import { supabase } from '../supabase';

/**
 * Upload photo with EXIF stripping via Edge Function
 * This ensures no location or metadata is leaked
 */
export const uploadReviewPhoto = async (file: File): Promise<{
  url: string;
  path: string;
  exifStripped: boolean;
}> => {
  try {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload JPEG, PNG, or WebP images only.');
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('File too large. Maximum size is 5MB.');
    }

    // Get current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error('Session error:', sessionError);
      throw new Error('Authentication error. Please try logging in again.');
    }
    
    if (!session || !session.access_token) {
      console.error('No session found. User needs to log in.');
      throw new Error('You must be logged in to upload photos. Please sign in and try again.');
    }
    
    console.log('✅ Session found for photo upload, user:', session.user?.email);

    // Get Supabase project URL from config
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://pgzuccyjbfzuhpeodwis.supabase.co';
    
    // Create FormData
    const formData = new FormData();
    formData.append('file', file);

    // Call Edge Function
    const response = await fetch(
      `${supabaseUrl}/functions/v1/upload-photo`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: formData,
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Upload failed');
    }

    const data = await response.json();

    return {
      url: data.publicUrl,
      path: data.path,
      exifStripped: data.exifStripped,
    };
  } catch (error: any) {
    console.error('Photo upload error:', error);
    throw new Error(error.message || 'Failed to upload photo');
  }
};

/**
 * Delete review photo
 */
export const deleteReviewPhoto = async (path: string): Promise<void> => {
  try {
    const { error } = await supabase.storage
      .from('review_photos')
      .remove([path]);

    if (error) {
      throw error;
    }
  } catch (error: any) {
    console.error('Photo delete error:', error);
    throw new Error(error.message || 'Failed to delete photo');
  }
};

/**
 * Get signed URL for private photo (if bucket is private)
 */
export const getSignedPhotoUrl = async (path: string, expiresIn: number = 3600): Promise<string> => {
  try {
    const { data, error } = await supabase.storage
      .from('review_photos')
      .createSignedUrl(path, expiresIn);

    if (error) {
      throw error;
    }

    return data.signedUrl;
  } catch (error: any) {
    console.error('Signed URL error:', error);
    throw new Error(error.message || 'Failed to get signed URL');
  }
};
